import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledForm from 'Components/StyledForm'
import { PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ProductManagement/constants'
import ActivePeriodWholeDayInput from 'Services/ProductManagement/Features/Product/Components/ActivePeriodWholeDayInput'
import { useUpdateSingleProductMutation } from 'Services/ProductManagement/Features/Product/productApi'
import {
  DaysOfTheWeek,
  type Product,
  ProductTabType,
  type Validity
} from 'Services/ProductManagement/Features/Product/types'
import {
  isWholeDay,
  WHOLE_DAY_FROM,
  WHOLE_DAY_UNTIL
} from 'Services/ProductManagement/Features/Product/utils'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Button, FormControlLabel, FormGroup, Switch, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

type DayType = 'EVERYDAY' | 'WEEKDAYS' | 'SATURDAY' | 'SUNDAY'

interface ProductAvailability {
  product: Product
  successBaseUrl: string
  handleClose: () => void
}

export default function ProductAvailability({
  product,
  successBaseUrl,
  handleClose
}: Readonly<ProductAvailability>) {
  const navigate = useNavigate()
  const { hasUnsavedChanges, markEdited, clearEdited } = useUnsavedChanges()
  const [updateSingleProduct, { isLoading: isSubmitLoading }] = useUpdateSingleProductMutation()

  const [initialActivePeriods, setInitialActivePeriods] = useState<Validity[]>(
    product.activation_window.period ?? []
  )
  const [activePeriods, setActivePeriods] = useState<Validity[]>(
    product.activation_window.period ?? []
  )

  const everyDayActivePeriod = activePeriods.find(period => period.days_of_week.length === 7)
  const weekDayActivePeriod = activePeriods.find(
    period =>
      period.days_of_week.length === 5 &&
      period.days_of_week.every(day =>
        [
          DaysOfTheWeek.MONDAY,
          DaysOfTheWeek.TUESDAY,
          DaysOfTheWeek.WEDNESDAY,
          DaysOfTheWeek.THURSDAY,
          DaysOfTheWeek.FRIDAY
        ].includes(day)
      )
  )
  const saturdayActivePeriod = activePeriods.find(
    period =>
      period.days_of_week.length === 1 && period.days_of_week.includes(DaysOfTheWeek.SATURDAY)
  )
  const sundayActivePeriod = activePeriods.find(
    period => period.days_of_week.length === 1 && period.days_of_week.includes(DaysOfTheWeek.SUNDAY)
  )

  const [noRestrictionsChecked, setNoRestrictionsChecked] = useState(
    initialActivePeriods.length === 0
  )
  const [everyDayChecked, setEveryDayChecked] = useState(everyDayActivePeriod !== undefined)
  const [weekDaysChecked, setWeekDaysChecked] = useState(weekDayActivePeriod !== undefined)
  const [saturdayChecked, setSaturdayChecked] = useState(saturdayActivePeriod !== undefined)
  const [sundayChecked, setSundayChecked] = useState(sundayActivePeriod !== undefined)

  const [everyDayWholeDayChecked, setEveryDayWholeDayChecked] = useState(
    everyDayActivePeriod
      ? isWholeDay(everyDayActivePeriod.time_of_day_from, everyDayActivePeriod.time_of_day_until)
      : true
  )
  const [weekDayWholeDayChecked, setWeekDayWholeDayChecked] = useState(
    weekDayActivePeriod
      ? isWholeDay(weekDayActivePeriod.time_of_day_from, weekDayActivePeriod.time_of_day_until)
      : true
  )
  const [saturdayWholeDayChecked, setSaturdayWholeDayChecked] = useState(
    saturdayActivePeriod
      ? isWholeDay(saturdayActivePeriod.time_of_day_from, saturdayActivePeriod.time_of_day_until)
      : true
  )
  const [sundayWholeDayChecked, setSundayWholeDayChecked] = useState(
    sundayActivePeriod
      ? isWholeDay(sundayActivePeriod.time_of_day_from, sundayActivePeriod.time_of_day_until)
      : true
  )

  /**
   * Synchronizes the state of day selections based on the current checks.
   */
  const syncDaySelections = () => {
    if (weekDaysChecked && saturdayChecked && sundayChecked) {
      setEveryDayChecked(true)
      setActivePeriods([
        {
          days_of_week: [
            DaysOfTheWeek.MONDAY,
            DaysOfTheWeek.TUESDAY,
            DaysOfTheWeek.WEDNESDAY,
            DaysOfTheWeek.THURSDAY,
            DaysOfTheWeek.FRIDAY,
            DaysOfTheWeek.SATURDAY,
            DaysOfTheWeek.SUNDAY
          ],
          exceptions: [],
          time_of_day_from: WHOLE_DAY_FROM,
          time_of_day_until: WHOLE_DAY_UNTIL,
          title: '',
          valid_from: '',
          valid_now: true,
          valid_until: '',
          zone_id: null
        }
      ])
      setWeekDaysChecked(false)
      setSaturdayChecked(false)
      setSundayChecked(false)
      // New merged period is whole-day; reset checkbox states to stay in sync.
      setEveryDayWholeDayChecked(true)
      setWeekDayWholeDayChecked(true)
      setSaturdayWholeDayChecked(true)
      setSundayWholeDayChecked(true)
    }

    if (everyDayChecked) {
      setWeekDaysChecked(false)
      setSaturdayChecked(false)
      setSundayChecked(false)
    }

    if (!everyDayChecked && !weekDaysChecked && !saturdayChecked && !sundayChecked) {
      setNoRestrictionsChecked(true)
      setActivePeriods([])
    }
  }

  useEffect(() => {
    syncDaySelections()
  }, [everyDayChecked, weekDaysChecked, saturdayChecked, sundayChecked])

  const handleNoRestrictionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setNoRestrictionsChecked(checked)
    setActivePeriods([])
    setEveryDayChecked(false)
    setWeekDaysChecked(false)
    setSaturdayChecked(false)
    setSundayChecked(false)
    markEdited()
  }

  // Check if any of the specified days are not included in the period
  const checkIfMissingAnyDay = (daysToCheck: string[], periodDays: string[]): boolean => {
    return daysToCheck.some(day => !periodDays.includes(day))
  }

  /**
   * Filters active periods by the specified days of the week and expected length.
   *
   * Removes any period that either:
   *   - Does not include all of the specified days (i.e., is missing any day in `daysOfWeek`)
   *   - Has a number of days that does not match `exactLength`
   *
   * This ensures that only periods which exactly match the specified days and length remain.
   */
  const filterActivePeriodsByDays = (daysOfWeek: string[], exactLength: number) => {
    setActivePeriods(prevState =>
      prevState.filter(period => {
        // Check if the period doesn't contain all the specified days
        // OR if the length doesn't match the expected length
        const hasDifferentLength = period.days_of_week.length !== exactLength

        const missingAnyDay = checkIfMissingAnyDay(daysOfWeek, period.days_of_week)

        return missingAnyDay || hasDifferentLength
      })
    )
  }

  const everyDayWholeDayCheckedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    activePeriod: Validity
  ) => {
    const { checked } = event.target
    setEveryDayWholeDayChecked(checked)
    // Checked: full-day range. Unchecked: clear so pickers start empty.
    setActivePeriods(prevState =>
      prevState.map(period =>
        period === activePeriod
          ? {
              ...period,
              time_of_day_from: checked ? WHOLE_DAY_FROM : null,
              time_of_day_until: checked ? WHOLE_DAY_UNTIL : null
            }
          : period
      )
    )
    markEdited()
  }

  const weekDayWholeDayCheckedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    activePeriod: Validity
  ) => {
    const { checked } = event.target
    setWeekDayWholeDayChecked(checked)
    // Checked: full-day range. Unchecked: clear so pickers start empty.
    setActivePeriods(prevState =>
      prevState.map(period =>
        period === activePeriod
          ? {
              ...period,
              time_of_day_from: checked ? WHOLE_DAY_FROM : null,
              time_of_day_until: checked ? WHOLE_DAY_UNTIL : null
            }
          : period
      )
    )
    markEdited()
  }

  const saturdayWholeDayCheckedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    activePeriod: Validity
  ) => {
    const { checked } = event.target
    setSaturdayWholeDayChecked(checked)
    // Checked: full-day range. Unchecked: clear so pickers start empty.
    setActivePeriods(prevState =>
      prevState.map(period =>
        period === activePeriod
          ? {
              ...period,
              time_of_day_from: checked ? WHOLE_DAY_FROM : null,
              time_of_day_until: checked ? WHOLE_DAY_UNTIL : null
            }
          : period
      )
    )
    markEdited()
  }

  const sundayWholeDayCheckedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    activePeriod: Validity
  ) => {
    const { checked } = event.target
    setSundayWholeDayChecked(checked)
    // Checked: full-day range. Unchecked: clear so pickers start empty.
    setActivePeriods(prevState =>
      prevState.map(period =>
        period === activePeriod
          ? {
              ...period,
              time_of_day_from: checked ? WHOLE_DAY_FROM : null,
              time_of_day_until: checked ? WHOLE_DAY_UNTIL : null
            }
          : period
      )
    )
    markEdited()
  }

  const dayTypeConfig: Record<
    DayType,
    {
      days: DaysOfTheWeek[]
      length: number
      setChecked: (checked: boolean) => void
      setWholeDayChecked: (checked: boolean) => void
    }
  > = {
    EVERYDAY: {
      days: [
        DaysOfTheWeek.MONDAY,
        DaysOfTheWeek.TUESDAY,
        DaysOfTheWeek.WEDNESDAY,
        DaysOfTheWeek.THURSDAY,
        DaysOfTheWeek.FRIDAY,
        DaysOfTheWeek.SATURDAY,
        DaysOfTheWeek.SUNDAY
      ],
      length: 7,
      setChecked: setEveryDayChecked,
      setWholeDayChecked: setEveryDayWholeDayChecked
    },
    WEEKDAYS: {
      days: [
        DaysOfTheWeek.MONDAY,
        DaysOfTheWeek.TUESDAY,
        DaysOfTheWeek.WEDNESDAY,
        DaysOfTheWeek.THURSDAY,
        DaysOfTheWeek.FRIDAY
      ],
      length: 5,
      setChecked: setWeekDaysChecked,
      setWholeDayChecked: setWeekDayWholeDayChecked
    },
    SATURDAY: {
      days: [DaysOfTheWeek.SATURDAY],
      length: 1,
      setChecked: setSaturdayChecked,
      setWholeDayChecked: setSaturdayWholeDayChecked
    },
    SUNDAY: {
      days: [DaysOfTheWeek.SUNDAY],
      length: 1,
      setChecked: setSundayChecked,
      setWholeDayChecked: setSundayWholeDayChecked
    }
  }

  const handleValidityChange =
    (dayType: DayType) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target
      const config = dayTypeConfig[dayType]
      if (checked) {
        setActivePeriods([
          ...activePeriods,
          {
            days_of_week: config.days,
            exceptions: [],
            time_of_day_from: WHOLE_DAY_FROM,
            time_of_day_until: WHOLE_DAY_UNTIL,
            title: '',
            valid_from: '',
            valid_now: true,
            valid_until: '',
            zone_id: null
          }
        ])
        // New period is whole-day; keep its checkbox in sync.
        config.setWholeDayChecked(true)
      } else {
        filterActivePeriodsByDays(config.days, config.length)
      }
      config.setChecked(checked)
      markEdited()
    }

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const productData = {
      ...product,
      activation_window: {
        ...product.activation_window,
        period: activePeriods
      }
    }

    updateSingleProduct({ ...productData, id: product.id })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Product successfully updated.`)
        setInitialActivePeriods(activePeriods)
        clearEdited()
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY]: ProductTabType.AVAILABILITY
          })
        )
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error updating the product.`)
      })
  }

  const handleTimePickerChange = (value: Date | null, name: string, periodToUpdate: Validity) => {
    if (value instanceof Date) {
      // Format time to HH:MM:SS
      const hours = value.getHours().toString().padStart(2, '0')
      const minutes = value.getMinutes().toString().padStart(2, '0')
      const seconds = value.getSeconds().toString().padStart(2, '0')
      const timeValue = `${hours}:${minutes}:${seconds}`

      setActivePeriods(prevPeriods => {
        return prevPeriods.map(period => {
          if (period === periodToUpdate) {
            return { ...period, [name]: timeValue }
          }
          return period
        })
      })
    }
    markEdited()
  }

  return (
    <StyledForm onSubmit={handleSave} isDisabled={isSubmitLoading}>
      <Box p={2}>
        <Typography variant="subtitle2" fontWeight="bold">
          {t`Product's daily availability`}
        </Typography>
        <FormGroup>
          <Typography variant="body2" mb={1}>
            {t`The ticket is available by default on all days and times. 
            You can limit the availability of the ticket by selecting specific availability 
            dates from the options below.`}
          </Typography>
          <Box ml={2} display="flex" flexDirection="column">
            <FormControlLabel
              control={
                <Switch onChange={handleNoRestrictionsChange} checked={noRestrictionsChecked} />
              }
              label={t`No restrictions (Default)`}
            />
            <FormControlLabel
              control={
                <Switch
                  onChange={handleValidityChange('EVERYDAY')}
                  disabled={
                    weekDaysChecked || saturdayChecked || sundayChecked || noRestrictionsChecked
                  }
                  checked={everyDayChecked}
                />
              }
              label={t`Everyday`}
            />
            <Box ml={1}>
              {everyDayChecked && everyDayActivePeriod && (
                <ActivePeriodWholeDayInput
                  isChecked={everyDayWholeDayChecked}
                  handleCheckedChange={e => everyDayWholeDayCheckedChange(e, everyDayActivePeriod)}
                  activePeriod={everyDayActivePeriod}
                  handleTimePickerChange={handleTimePickerChange}
                />
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  onChange={handleValidityChange('WEEKDAYS')}
                  disabled={everyDayChecked || noRestrictionsChecked}
                  checked={weekDaysChecked}
                />
              }
              label={t`Weekdays`}
            />
            <Box ml={1}>
              {weekDaysChecked && weekDayActivePeriod && (
                <ActivePeriodWholeDayInput
                  isChecked={weekDayWholeDayChecked}
                  handleCheckedChange={e => weekDayWholeDayCheckedChange(e, weekDayActivePeriod)}
                  activePeriod={weekDayActivePeriod}
                  handleTimePickerChange={handleTimePickerChange}
                />
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  onChange={handleValidityChange('SATURDAY')}
                  disabled={everyDayChecked || noRestrictionsChecked}
                  checked={saturdayChecked}
                />
              }
              label={t`Saturday`}
            />
            <Box ml={1}>
              {saturdayChecked && saturdayActivePeriod && (
                <ActivePeriodWholeDayInput
                  isChecked={saturdayWholeDayChecked}
                  handleCheckedChange={e => saturdayWholeDayCheckedChange(e, saturdayActivePeriod)}
                  activePeriod={saturdayActivePeriod}
                  handleTimePickerChange={handleTimePickerChange}
                />
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  onChange={handleValidityChange('SUNDAY')}
                  disabled={everyDayChecked || noRestrictionsChecked}
                  checked={sundayChecked}
                />
              }
              label={t`Sunday`}
            />
            <Box ml={1}>
              {sundayChecked && sundayActivePeriod && (
                <ActivePeriodWholeDayInput
                  isChecked={sundayWholeDayChecked}
                  handleCheckedChange={e => sundayWholeDayCheckedChange(e, sundayActivePeriod)}
                  activePeriod={sundayActivePeriod}
                  handleTimePickerChange={handleTimePickerChange}
                />
              )}
            </Box>
          </Box>
        </FormGroup>
        <Box display="flex" gap={2} mt={2}>
          <Button
            onClick={handleClose}
            size="medium"
            variant="outlined"
            color="inherit"
            sx={{ mt: 2 }}
          >
            {t`Cancel`}
          </Button>
          <CustomLoadingButton
            size="medium"
            type="submit"
            disabled={!hasUnsavedChanges}
            isLoading={isSubmitLoading}
            iconPosition="end"
          >
            {t`Save changes`}
          </CustomLoadingButton>
        </Box>
      </Box>
    </StyledForm>
  )
}
